import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { BASE_URL, HTTP_STATUS } from '../../utils/constant'
import { newAxiosInstance } from '../../../axios'
import UrlBuilder from 'app/utils/UrlBuilder'

const normalizeEntry = (entry) => ({
    id:               entry.id,
    workforceProfile: entry.workforceProfile || null,
    employee:         entry.workforceProfile
                          ? { id: entry.workforceProfile.id, label: entry.workforceProfile.name }
                          : (entry.employeeId != null ? { id: entry.employeeId, label: entry.employeeName } : entry.employeeName),
    name:             entry.workforceProfile?.name || entry.employeeName || '',
    tier:             entry.workforceProfile?.tier || entry.tier || '',
    year:             entry.year,
    month:            entry.month,
    salary:           entry.salary        || 0,
    basic:            entry.basic         || 0,
    comm:             entry.comm          || 0,
    attend:           entry.attend        || 0,
    short:            entry.short             || 0,
    advanceDeduction: entry.advanceDeduction  || 0,
    loanDeduction:    entry.loanDeduction     || 0,
    epf:              entry.epf               || 0,
    customValues:     entry.customValues      || {},
    isPosted:         entry.isPosted          || false,
    isLocked:         entry.isLocked          ?? false,
    postingDate:      entry.postingDate        || null,
    glAccounts:       entry.glAccounts         || {},
    payableDate:      entry.payableDate        || null,
    paidAmount:       entry.paidAmount         || 0,
});

const normalizeLoan = (loan) => ({
    id:               loan.id,
    workforceProfile: loan.workforceProfile || null,
    name:             loan.workforceProfile?.name || loan.employeeName || '',
    principal:        loan.principal,
    interest:         loan.interest,
    terms:            loan.terms,
    dt:               loan.dt || '',
    deductions:       loan.deductions || [],
    isPosted:         loan.isPosted    || false,
});

const initialState = {
    payrollEntries: [],
    advanceEntries: [],
    loanEntries:    [],
    customFields: {
        additions:  [],
        deductions: [],
    },
    status:        HTTP_STATUS.IDLE,
    advanceStatus: HTTP_STATUS.IDLE,
    loanStatus:    HTTP_STATUS.IDLE,
    deleteStatus:  HTTP_STATUS.IDLE,
    lockStatus:    HTTP_STATUS.IDLE,
    profilePayroll:        [],
    profilePayrollStatus:  HTTP_STATUS.IDLE,
    profileAdvances:       [],
    profileAdvancesStatus: HTTP_STATUS.IDLE,
    profileLoans:          [],
    profileLoansStatus:    HTTP_STATUS.IDLE,
    advanceReport:       null,
    advanceReportStatus: HTTP_STATUS.IDLE,
    loanReport:          null,
    loanReportStatus:    HTTP_STATUS.IDLE,
}

// --- Payroll thunks ---

export const fetchPayrollEntries = createAsyncThunk('payroll/get-by-period', async ({ year, month }, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.get(BASE_URL + `/api/Payroll/get-by-period?year=${year}&month=${month}`)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const upsertPayrollEntry = createAsyncThunk('payroll/upsert', async (body, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.post(BASE_URL + '/api/Payroll', body)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response)
    }
})

export const updatePayrollEntry = createAsyncThunk('payroll/update', async ({ id, ...body }, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.put(BASE_URL + `/api/Payroll/${id}`, body)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response)
    }
})

export const fetchPayrollEntryById = createAsyncThunk('payroll/get-by-id', async (id, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.get(BASE_URL + `/api/Payroll/${id}`)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const postPayrollPayment = createAsyncThunk('payroll/payment/post', async ({ id, ...body }, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.post(BASE_URL + `/api/Payroll/${id}/payment`, body)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response)
    }
})

export const removePayrollEntry = createAsyncThunk('payroll/remove', async (id, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.delete(BASE_URL + `/api/Payroll/${id}`)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const fetchCustomFields = createAsyncThunk('payroll/fetch-custom-fields', async (_, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.get(BASE_URL + '/api/PayrollCustomField')
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const createCustomField = createAsyncThunk('payroll/create-custom-field', async (body, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.post(BASE_URL + '/api/PayrollCustomField', body)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response)
    }
})

export const deleteCustomField = createAsyncThunk('payroll/delete-custom-field', async ({ id, side }, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.delete(BASE_URL + `/api/PayrollCustomField/${id}`)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const lockPayrollEntry = createAsyncThunk('payroll/lock-entry', async (id, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.patch(BASE_URL + `/api/Payroll/${id}/lock`)
        return normalizeEntry(res.data)
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response)
    }
})

// --- Profile-scoped thunks (WorkforceProfile tabs) ---

export const fetchProfilePayroll = createAsyncThunk('payroll/fetchByProfile', async ({ profileId, from, to }, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.get(BASE_URL + `/api/Payroll/by-profile/${profileId}?from=${from}&to=${to}`)
        return res.data
    } catch (err) {
        if (!err.response) throw err
        return rejectWithValue(err.response.data)
    }
})

export const fetchProfileAdvances = createAsyncThunk('payroll/advance/fetchByProfile', async ({ profileId, from, to }, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.get(BASE_URL + `/api/Advance/by-profile/${profileId}?from=${from}&to=${to}`)
        return res.data
    } catch (err) {
        if (!err.response) throw err
        return rejectWithValue(err.response.data)
    }
})

export const fetchProfileLoans = createAsyncThunk('payroll/loan/fetchByProfile', async ({ profileId, from, to }, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.get(BASE_URL + `/api/StaffLoan/by-profile/${profileId}?from=${from}&to=${to}`)
        return res.data
    } catch (err) {
        if (!err.response) throw err
        return rejectWithValue(err.response.data)
    }
})

// --- Advance thunks ---

export const fetchAdvanceEntries = createAsyncThunk('payroll/advance/get-by-period', async ({ year, month }, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.get(BASE_URL + `/api/Advance/get-by-period?year=${year}&month=${month}`)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const createAdvanceLog = createAsyncThunk('payroll/advance/create', async (body, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.post(BASE_URL + '/api/Advance', body)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response)
    }
})

export const editAdvanceLog = createAsyncThunk('payroll/advance/edit', async ({ id, ...body }, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.put(BASE_URL + `/api/Advance/${id}`, body)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response)
    }
})

export const deleteAdvanceLog = createAsyncThunk('payroll/advance/delete', async (id, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.delete(BASE_URL + `/api/Advance/${id}`)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

// --- Staff Loan thunks ---

export const fetchLoanEntries = createAsyncThunk('payroll/loan/active', async (_, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.get(BASE_URL + '/api/StaffLoan/active')
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const createLoanEntry = createAsyncThunk('payroll/loan/create', async (body, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.post(BASE_URL + '/api/StaffLoan', body)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response)
    }
})

export const editLoanEntry = createAsyncThunk('payroll/loan/edit', async ({ id, ...body }, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.put(BASE_URL + `/api/StaffLoan/${id}`, body)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response)
    }
})

export const deleteLoanEntry = createAsyncThunk('payroll/loan/delete', async (id, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.delete(BASE_URL + `/api/StaffLoan/${id}`)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const fetchLoanDeductions = createAsyncThunk('payroll/loan/fetch-deductions', async (loanId, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.get(BASE_URL + `/api/StaffLoan/${loanId}/deductions`)
        return { loanId, deductions: res.data }
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const fetchAdvanceReport = createAsyncThunk('payroll/advance/daily-report', async (data = null, { rejectWithValue }) => {
    try {
        let url = new UrlBuilder().filter(data, 'fileType').filter(data, 'reportDate').getUrl();
        const response = await newAxiosInstance.get(BASE_URL + `/api/Advance/daily-report${url}`, { responseType: 'text' });
        return response.data;
    } catch (err) {
        if (!err.response) throw err;
        return rejectWithValue(err.response.data);
    }
})

export const fetchLoanReport = createAsyncThunk('payroll/loan/daily-report', async (data = null, { rejectWithValue }) => {
    try {
        let url = new UrlBuilder().filter(data, 'fileType').filter(data, 'reportDate').getUrl();
        const response = await newAxiosInstance.get(BASE_URL + `/api/StaffLoan/daily-report${url}`, { responseType: 'text' });
        return response.data;
    } catch (err) {
        if (!err.response) throw err;
        return rejectWithValue(err.response.data);
    }
})

export const deleteLoanDeduction = createAsyncThunk('payroll/loan/delete-deduction', async ({ loanId, deductionId }, { rejectWithValue }) => {
    try {
        const res = await newAxiosInstance.delete(BASE_URL + `/api/StaffLoan/deduction/${deductionId}`)
        return res.data
    } catch(err) {
        if(!err.response) throw err;
        return rejectWithValue(err.response.data)
    }
})

export const Payroll = createSlice({
    name: 'payroll',
    initialState,
    reducers: {
        resetState: () => initialState,
    },
    extraReducers(builder) {
        builder
            // Payroll entries
            .addCase(fetchPayrollEntries.pending, (state) => {
                state.status = HTTP_STATUS.LOADING
                state.payrollEntries = []
            })
            .addCase(fetchPayrollEntries.fulfilled, (state, action) => {
                state.status = HTTP_STATUS.SUCCEEDED
                state.payrollEntries = action.payload.map(normalizeEntry)
            })
            .addCase(fetchPayrollEntries.rejected, (state) => {
                state.status = HTTP_STATUS.FAILED
            })
            .addCase(upsertPayrollEntry.pending, (state) => {
                state.status = HTTP_STATUS.LOADING
            })
            .addCase(upsertPayrollEntry.fulfilled, (state, action) => {
                state.status = HTTP_STATUS.SUCCEEDED
                const normalized = normalizeEntry(action.payload)
                const idx = state.payrollEntries.findIndex(p => p.id === normalized.id)
                if (idx > -1) state.payrollEntries[idx] = normalized;
                else state.payrollEntries.push(normalized);
            })
            .addCase(upsertPayrollEntry.rejected, (state) => {
                state.status = HTTP_STATUS.FAILED
            })
            .addCase(updatePayrollEntry.pending, (state) => {
                state.status = HTTP_STATUS.LOADING
            })
            .addCase(updatePayrollEntry.fulfilled, (state, action) => {
                state.status = HTTP_STATUS.SUCCEEDED
                const normalized = normalizeEntry(action.payload)
                const idx = state.payrollEntries.findIndex(p => p.id === normalized.id)
                if (idx > -1) state.payrollEntries[idx] = normalized;
            })
            .addCase(updatePayrollEntry.rejected, (state) => {
                state.status = HTTP_STATUS.FAILED
            })
            .addCase(removePayrollEntry.pending, (state) => {
                state.deleteStatus = HTTP_STATUS.LOADING
            })
            .addCase(removePayrollEntry.fulfilled, (state, action) => {
                state.deleteStatus = HTTP_STATUS.SUCCEEDED
                state.payrollEntries = state.payrollEntries.filter(p => p.id !== action.meta.arg)
            })
            .addCase(removePayrollEntry.rejected, (state) => {
                state.deleteStatus = HTTP_STATUS.FAILED
            })
            .addCase(fetchPayrollEntryById.fulfilled, (state, action) => {
                const normalized = normalizeEntry(action.payload)
                const idx = state.payrollEntries.findIndex(p => p.id === normalized.id)
                if (idx > -1) state.payrollEntries[idx] = normalized;
            })
            .addCase(postPayrollPayment.fulfilled, (state, action) => {
                const payroll = action.payload?.payroll
                if (payroll) {
                    const normalized = normalizeEntry(payroll)
                    const idx = state.payrollEntries.findIndex(p => p.id === normalized.id)
                    if (idx > -1) state.payrollEntries[idx] = normalized;
                }
            })
            // Custom fields
            .addCase(fetchCustomFields.fulfilled, (state, action) => {
                state.customFields = action.payload
            })
            .addCase(createCustomField.fulfilled, (state, action) => {
                const { id, side, label } = action.payload
                state.customFields[side].push({ id, label })
                state.payrollEntries.forEach(entry => {
                    entry.customValues = { ...(entry.customValues || {}), [id]: 0 }
                })
            })
            .addCase(deleteCustomField.fulfilled, (state, action) => {
                const { id, side } = action.meta.arg
                state.customFields[side] = state.customFields[side].filter(f => f.id !== id)
                state.payrollEntries.forEach(entry => {
                    if (entry.customValues) delete entry.customValues[id]
                })
            })
            // Advance entries
            .addCase(fetchAdvanceEntries.pending, (state) => {
                state.advanceStatus = HTTP_STATUS.LOADING
            })
            .addCase(fetchAdvanceEntries.fulfilled, (state, action) => {
                state.advanceStatus = HTTP_STATUS.SUCCEEDED
                const map = new Map()
                action.payload.forEach(item => {
                    const name = item.workforceProfile?.name || item.employeeName || '';
                    const tier = item.workforceProfile?.tier || item.tier || '';
                    if (!map.has(name))
                        map.set(name, { name, workforceProfile: item.workforceProfile || null, tier, logs: [] })
                    map.get(name).logs.push({ id: item.id, amt: item.amount, dt: item.date, isPosted: item.isPosted || false, postingDate: item.postingDate || null, narration: item.narration || null })
                })
                state.advanceEntries = Array.from(map.values())
            })
            .addCase(fetchAdvanceEntries.rejected, (state) => {
                state.advanceStatus = HTTP_STATUS.FAILED
            })
            .addCase(createAdvanceLog.pending, (state) => {
                state.advanceStatus = HTTP_STATUS.LOADING
            })
            .addCase(createAdvanceLog.fulfilled, (state, action) => {
                state.advanceStatus = HTTP_STATUS.SUCCEEDED
                const item = action.payload
                const name = item.workforceProfile?.name || item.employeeName || '';
                const tier = item.workforceProfile?.tier || item.tier || '';
                const entry = state.advanceEntries.find(a => a.name.toLowerCase() === name.toLowerCase())
                if (entry) {
                    entry.logs.push({ id: item.id, amt: item.amount, dt: item.date, isPosted: item.isPosted || false, postingDate: item.postingDate || null, narration: item.narration || null })
                    entry.tier = tier
                    entry.workforceProfile = item.workforceProfile || null
                } else {
                    state.advanceEntries.push({ name, workforceProfile: item.workforceProfile || null, tier, logs: [{ id: item.id, amt: item.amount, dt: item.date, isPosted: item.isPosted || false, postingDate: item.postingDate || null, narration: item.narration || null }] })
                }
            })
            .addCase(createAdvanceLog.rejected, (state) => {
                state.advanceStatus = HTTP_STATUS.FAILED
            })
            .addCase(editAdvanceLog.pending, (state) => {
                state.advanceStatus = HTTP_STATUS.LOADING
            })
            .addCase(editAdvanceLog.fulfilled, (state, action) => {
                state.advanceStatus = HTTP_STATUS.SUCCEEDED
                const item = action.payload
                const name = item.workforceProfile?.name || item.employeeName || '';
                const entry = state.advanceEntries.find(a => a.name.toLowerCase() === name.toLowerCase())
                if (entry) {
                    const log = entry.logs.find(l => l.id === item.id)
                    if (log) { log.amt = item.amount; log.dt = item.date; }
                }
            })
            .addCase(editAdvanceLog.rejected, (state) => {
                state.advanceStatus = HTTP_STATUS.FAILED
            })
            .addCase(deleteAdvanceLog.pending, (state) => {
                state.deleteStatus = HTTP_STATUS.LOADING
            })
            .addCase(deleteAdvanceLog.fulfilled, (state, action) => {
                state.deleteStatus = HTTP_STATUS.SUCCEEDED
                const id = action.meta.arg
                state.advanceEntries = state.advanceEntries
                    .map(e => ({ ...e, logs: e.logs.filter(l => l.id !== id) }))
                    .filter(e => e.logs.length > 0)
            })
            .addCase(deleteAdvanceLog.rejected, (state) => {
                state.deleteStatus = HTTP_STATUS.FAILED
            })
            // Loan entries
            .addCase(fetchLoanEntries.pending, (state) => {
                state.loanStatus = HTTP_STATUS.LOADING
            })
            .addCase(fetchLoanEntries.fulfilled, (state, action) => {
                state.loanStatus = HTTP_STATUS.SUCCEEDED
                state.loanEntries = action.payload.map(normalizeLoan)
            })
            .addCase(fetchLoanEntries.rejected, (state) => {
                state.loanStatus = HTTP_STATUS.FAILED
            })
            .addCase(createLoanEntry.pending, (state) => {
                state.loanStatus = HTTP_STATUS.LOADING
            })
            .addCase(createLoanEntry.fulfilled, (state, action) => {
                state.loanStatus = HTTP_STATUS.SUCCEEDED
                state.loanEntries.push(normalizeLoan(action.payload))
            })
            .addCase(createLoanEntry.rejected, (state) => {
                state.loanStatus = HTTP_STATUS.FAILED
            })
            .addCase(editLoanEntry.pending, (state) => {
                state.loanStatus = HTTP_STATUS.LOADING
            })
            .addCase(editLoanEntry.fulfilled, (state, action) => {
                state.loanStatus = HTTP_STATUS.SUCCEEDED
                const normalized = normalizeLoan(action.payload)
                const idx = state.loanEntries.findIndex(l => l.id === normalized.id)
                if (idx > -1) state.loanEntries[idx] = normalized
            })
            .addCase(editLoanEntry.rejected, (state) => {
                state.loanStatus = HTTP_STATUS.FAILED
            })
            .addCase(deleteLoanEntry.pending, (state) => {
                state.deleteStatus = HTTP_STATUS.LOADING
            })
            .addCase(deleteLoanEntry.fulfilled, (state, action) => {
                state.deleteStatus = HTTP_STATUS.SUCCEEDED
                state.loanEntries = state.loanEntries.filter(l => l.id !== action.meta.arg)
            })
            .addCase(deleteLoanEntry.rejected, (state) => {
                state.deleteStatus = HTTP_STATUS.FAILED
            })
            .addCase(fetchLoanDeductions.fulfilled, (state, action) => {
                const { loanId, deductions } = action.payload
                const loan = state.loanEntries.find(l => l.id === loanId)
                if (loan) loan.deductions = deductions
            })
            .addCase(deleteLoanDeduction.pending, (state) => {
                state.deleteStatus = HTTP_STATUS.LOADING
            })
            .addCase(deleteLoanDeduction.fulfilled, (state, action) => {
                state.deleteStatus = HTTP_STATUS.SUCCEEDED
                const { loanId, deductionId } = action.meta.arg
                const loan = state.loanEntries.find(l => l.id === loanId)
                if (loan) loan.deductions = loan.deductions.filter(d => d.id !== deductionId)
            })
            .addCase(deleteLoanDeduction.rejected, (state) => {
                state.deleteStatus = HTTP_STATUS.FAILED
            })
            // Lock payroll entry
            .addCase(lockPayrollEntry.pending, (state) => {
                state.lockStatus = HTTP_STATUS.LOADING
            })
            .addCase(lockPayrollEntry.fulfilled, (state, action) => {
                state.lockStatus = HTTP_STATUS.SUCCEEDED
                const idx = state.payrollEntries.findIndex(p => p.id === action.payload.id)
                if (idx > -1) state.payrollEntries[idx] = action.payload;
            })
            .addCase(lockPayrollEntry.rejected, (state) => {
                state.lockStatus = HTTP_STATUS.FAILED
            })
            // Profile-scoped payroll
            .addCase(fetchProfilePayroll.pending, (state) => {
                state.profilePayrollStatus = HTTP_STATUS.LOADING
                state.profilePayroll = []
            })
            .addCase(fetchProfilePayroll.fulfilled, (state, action) => {
                state.profilePayrollStatus = HTTP_STATUS.SUCCEEDED
                state.profilePayroll = action.payload.map(normalizeEntry)
            })
            .addCase(fetchProfilePayroll.rejected, (state) => {
                state.profilePayrollStatus = HTTP_STATUS.FAILED
            })
            // Profile-scoped advances
            .addCase(fetchProfileAdvances.pending, (state) => {
                state.profileAdvancesStatus = HTTP_STATUS.LOADING
                state.profileAdvances = []
            })
            .addCase(fetchProfileAdvances.fulfilled, (state, action) => {
                state.profileAdvancesStatus = HTTP_STATUS.SUCCEEDED
                state.profileAdvances = action.payload
            })
            .addCase(fetchProfileAdvances.rejected, (state) => {
                state.profileAdvancesStatus = HTTP_STATUS.FAILED
            })
            // Profile-scoped loans
            .addCase(fetchProfileLoans.pending, (state) => {
                state.profileLoansStatus = HTTP_STATUS.LOADING
                state.profileLoans = []
            })
            .addCase(fetchProfileLoans.fulfilled, (state, action) => {
                state.profileLoansStatus = HTTP_STATUS.SUCCEEDED
                state.profileLoans = action.payload.map(normalizeLoan)
            })
            .addCase(fetchProfileLoans.rejected, (state) => {
                state.profileLoansStatus = HTTP_STATUS.FAILED
            })
            // Advance daily report
            .addCase(fetchAdvanceReport.pending, (state) => {
                state.advanceReportStatus = HTTP_STATUS.LOADING
            })
            .addCase(fetchAdvanceReport.fulfilled, (state, action) => {
                state.advanceReportStatus = HTTP_STATUS.SUCCEEDED
                state.advanceReport = action.payload
            })
            .addCase(fetchAdvanceReport.rejected, (state) => {
                state.advanceReportStatus = HTTP_STATUS.FAILED
            })
            // Loan daily report
            .addCase(fetchLoanReport.pending, (state) => {
                state.loanReportStatus = HTTP_STATUS.LOADING
            })
            .addCase(fetchLoanReport.fulfilled, (state, action) => {
                state.loanReportStatus = HTTP_STATUS.SUCCEEDED
                state.loanReport = action.payload
            })
            .addCase(fetchLoanReport.rejected, (state) => {
                state.loanReportStatus = HTTP_STATUS.FAILED
            })
    }
})

export const getProfilePayroll        = (state) => state.payroll.profilePayroll;
export const getProfilePayrollStatus  = (state) => state.payroll.profilePayrollStatus;
export const getProfileAdvances       = (state) => state.payroll.profileAdvances;
export const getProfileAdvancesStatus = (state) => state.payroll.profileAdvancesStatus;
export const getProfileLoans          = (state) => state.payroll.profileLoans;
export const getProfileLoansStatus    = (state) => state.payroll.profileLoansStatus;
export const getPayrollEntries      = (state) => state.payroll.payrollEntries;
export const getAdvanceEntries      = (state) => state.payroll.advanceEntries;
export const getLoanEntries         = (state) => state.payroll.loanEntries;
export const getCustomFields        = (state) => state.payroll.customFields;
export const getPayrollStatus       = (state) => state.payroll.status;
export const getAdvanceStatus       = (state) => state.payroll.advanceStatus;
export const getLoanStatus          = (state) => state.payroll.loanStatus;
export const getDeletePayrollStatus = (state) => state.payroll.deleteStatus;
export const getLockPayrollStatus   = (state) => state.payroll.lockStatus;
export const getAdvanceReport       = (state) => state.payroll.advanceReport;
export const getAdvanceReportStatus = (state) => state.payroll.advanceReportStatus;
export const getLoanReport          = (state) => state.payroll.loanReport;
export const getLoanReportStatus    = (state) => state.payroll.loanReportStatus;
export const payrollActions         = Payroll.actions;

export default Payroll.reducer;
